import React, { useEffect, useRef, useState, startTransition } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  signInWithPhoneNumber,
  signInWithPopup,
  signInWithCustomToken,
  GoogleAuthProvider,
  RecaptchaVerifier,
  getIdToken,
} from "firebase/auth";

import css from "./LoginComponent.module.css";
import { auth } from "../../config/firebase";
import { getConfig } from "../../config";
import { useAuth } from "../../hooks/useAuth";
import { authActions } from "../../store/slices/auth-slices";
import { useDeviceDetection } from "../../hooks/useDeviceDetection";
import {
  validatePhoneNumberWithMessage,
  phoneNumberExamples,
  formatPhoneNumber
} from "../../helpers/phoneNumberValidator";

const LoginComponent = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const config = getConfig();
  const {
    status,
    error,
    isAuthenticated,
    initializationStatus,
    sendOtp: sendOtpAction,
    verifyOtp: verifyOtpAction,
    firebaseSignIn: firebaseSignInAction,
    clearError,
  } = useAuth();
  const { deviceName } = useDeviceDetection();

  const [phoneNumber, setPhoneNumber] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [recaptchaVerifier, setRecaptchaVerifier] = useState(null);
  const [authStep, setAuthStep] = useState("phone"); // 'phone' or 'code'
  const [isFallbackMode, setIsFallbackMode] = useState(false); // true when using Twilio fallback
  const [localError, setLocalError] = useState("");
  const [loading, setLoading] = useState(false);

  const otpRefs = useRef([]);

  // Setup reCAPTCHA verifier
  useEffect(() => {
    if (!recaptchaVerifier && authStep === "phone") {
      const initRecaptcha = async () => {
        const container = document.getElementById("recaptcha-container");
        if (!container) {
          if (process.env.NODE_ENV === 'development') {
            console.warn("reCAPTCHA container not found in DOM, waiting...");
          }
          return;
        }

        try {
          const verifier = new RecaptchaVerifier(auth, "recaptcha-container", {
            size: "invisible",
            callback: (response) => {
              if (process.env.NODE_ENV === 'development') {
                console.log("reCAPTCHA solved:", response ? "success" : "failed");
              }
            },
            "expired-callback": () => {
              if (process.env.NODE_ENV === 'development') {
                console.log("reCAPTCHA expired, need to solve again");
              }
              setLocalError("reCAPTCHA expired. Please try again.");
            },
          });

          await verifier.render();

          if (process.env.NODE_ENV === 'development') {
            console.log("reCAPTCHA initialized successfully");
          }

          setRecaptchaVerifier(verifier);
        } catch (error) {
          console.error("Error setting up reCAPTCHA:", error);
          setLocalError(
            "Failed to initialize reCAPTCHA. This may be due to network issues or browser extensions. " +
            "Please try refreshing the page or disabling ad blockers."
          );
        }
      };

      initRecaptcha();
    }

    return () => {
      if (recaptchaVerifier) {
        try {
          if (recaptchaVerifier && typeof recaptchaVerifier.clear === 'function') {
            recaptchaVerifier.clear();
          }
        } catch (error) {
          console.debug("reCAPTCHA cleanup:", error.message);
        }
      }
    };
  }, [authStep, recaptchaVerifier]);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      startTransition(() => {
        navigate("/dishes");
      });
    }
  }, [isAuthenticated, navigate]);

  // Clear errors when inputs change
  useEffect(() => {
    setLocalError("");
    clearError();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phoneNumber, verificationCode]);

  // Send verification code — try Firebase first, fall back to Twilio
  const handleSendCode = async (e) => {
    e.preventDefault();
    setLocalError("");
    setLoading(true);

    const formattedPhone = formatPhoneNumber(phoneNumber);
    setPhoneNumber(formattedPhone);

    const validation = validatePhoneNumberWithMessage(formattedPhone);
    if (!validation.isValid) {
      setLocalError(validation.error);
      setLoading(false);
      return;
    }

    // --- Primary: Firebase reCAPTCHA + signInWithPhoneNumber ---
    if (recaptchaVerifier) {
      try {
        const confirmation = await signInWithPhoneNumber(
          auth,
          formattedPhone,
          recaptchaVerifier
        );
        setConfirmationResult(confirmation);
        setIsFallbackMode(false);
        setAuthStep("code");
        setLocalError("");
        setLoading(false);
        return;
      } catch (firebaseError) {
        console.warn("Firebase OTP failed, falling back to Twilio:", firebaseError.message);

        try {
          recaptchaVerifier.clear();
        } catch (clearError) {
          console.debug("reCAPTCHA clear error:", clearError.message);
        }
        setRecaptchaVerifier(null);
      }
    }

    // --- Fallback: Twilio POST /auth/send-code ---
    try {
      await sendOtpAction({ phoneNumber: formattedPhone }).unwrap();
      setIsFallbackMode(true);
      setAuthStep("code");
      setLocalError("");
    } catch (twilioError) {
      console.error("Twilio OTP send failed:", twilioError);
      setLocalError(
        typeof twilioError === "string"
          ? twilioError
          : twilioError?.message || "Failed to send verification code. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  // Verify code and sign in
  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setLocalError("");
    setLoading(true);

    if (!verificationCode) {
      setLocalError("Please enter the verification code");
      setLoading(false);
      return;
    }

    try {
      dispatch(authActions.setPhoneNumber(phoneNumber));
      dispatch(authActions.setAuthMethod("phone"));

      if (!isFallbackMode) {
        // --- Primary path: Firebase confirmationResult.confirm ---
        if (!confirmationResult) {
          throw new Error("No confirmation result available");
        }

        const result = await confirmationResult.confirm(verificationCode);
        const idToken = await getIdToken(result.user);

        await firebaseSignInAction({ idToken, name: deviceName }).unwrap();

        if (recaptchaVerifier) {
          try {
            recaptchaVerifier.clear();
          } catch (cleanupError) {
            console.debug("reCAPTCHA cleanup:", cleanupError.message);
          }
          setRecaptchaVerifier(null);
        }
      } else {
        // --- Fallback path: Twilio verify → signInWithCustomToken → firebaseSignIn ---
        const verifyResponse = await verifyOtpAction({
          phoneNumber,
          code: verificationCode,
          name: deviceName,
        }).unwrap();

        const customToken =
          verifyResponse?.data?.idToken ?? verifyResponse?.idToken;

        if (!customToken) {
          throw new Error("No token received from verification");
        }

        const { user: firebaseUser } = await signInWithCustomToken(auth, customToken);
        const idToken = await getIdToken(firebaseUser);

        await firebaseSignInAction({ idToken, name: deviceName }).unwrap();
      }

      // Navigation handled by the isAuthenticated useEffect
    } catch (error) {
      console.error("Error verifying code:", error);
      setLocalError(getFirebaseErrorMessage(error.code) || error.message);
    } finally {
      setLoading(false);
    }
  };

  // Sign in with Google
  const handleGoogleSignIn = async () => {
    setLocalError("");
    setLoading(true);

    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      const idToken = await getIdToken(user);

      if (user.phoneNumber) {
        dispatch(authActions.setPhoneNumber(user.phoneNumber));
      }
      dispatch(authActions.setAuthMethod("google"));

      await firebaseSignInAction({
        idToken,
        name: deviceName,
      }).unwrap();

      if (recaptchaVerifier) {
        try {
          recaptchaVerifier.clear();
        } catch (error) {
          console.debug("reCAPTCHA cleanup:", error.message);
        }
        setRecaptchaVerifier(null);
      }
    } catch (error) {
      console.error("Error signing in with Google:", error);
      if (error.code !== "auth/popup-closed-by-user") {
        setLocalError(getFirebaseErrorMessage(error.code) || error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  // Go back to phone input
  const handleBackToPhone = () => {
    setAuthStep("phone");
    setVerificationCode("");
    setConfirmationResult(null);
    setIsFallbackMode(false);
    setLocalError("");

    if (recaptchaVerifier) {
      try {
        recaptchaVerifier.clear();
      } catch (error) {
        console.debug("reCAPTCHA clear error:", error.message);
      }
      setRecaptchaVerifier(null);
    }
  };

  // Firebase error messages
  const getFirebaseErrorMessage = (code) => {
    const errors = {
      "auth/invalid-phone-number": "Invalid phone number format",
      "auth/missing-phone-number": "Please enter a phone number",
      "auth/quota-exceeded": "SMS quota exceeded. Please try again later",
      "auth/user-disabled": "This account has been disabled",
      "auth/invalid-verification-code": "Invalid verification code",
      "auth/code-expired": "Verification code has expired",
      "auth/popup-closed-by-user": "Sign-in popup was closed",
      "auth/cancelled-popup-request": "Only one popup request is allowed at a time",
      "auth/popup-blocked": "Popup was blocked by the browser",
      "auth/account-exists-with-different-credential":
        "An account already exists with the same email",
      "auth/network-request-failed": "Network error. Please check your connection",
    };
    return errors[code] || "An authentication error occurred";
  };

  // OTP box UI helpers — purely visual, verificationCode state is unchanged
  const handleOtpBoxChange = (index, e) => {
    const val = e.target.value.replace(/\D/g, '').slice(-1);
    const arr = Array.from({ length: 6 }, (_, i) => verificationCode[i] || '');
    arr[index] = val;
    setVerificationCode(arr.join(''));
    if (val && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpBoxKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !e.target.value && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpBoxPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    setVerificationCode(pasted);
    setTimeout(() => {
      otpRefs.current[Math.min(pasted.length, 5)]?.focus();
    }, 0);
  };

  // Show loading state while checking authentication
  if (initializationStatus === "loading") {
    return (
      <div className={css.loadingWrap}>
        <div className={css.loadingSpinner}></div>
        <span>Checking authentication...</span>
      </div>
    );
  }

  return (
    <section className={css.wrap}>
      {/* Logo */}
      <div className={css.logoArea}>
        <img src={config.logo} alt={config.title} className={css.logo} />
      </div>

      {/* Step progress dots */}
      <div className={css.stepTrack}>
        <div
          className={`${css.stepDot} ${
            authStep === 'phone' ? css.stepDotActive : css.stepDotDone
          }`}
        />
        <div className={`${css.stepLine} ${authStep === 'code' ? css.stepLineFill : ''}`} />
        <div
          className={`${css.stepDot} ${authStep === 'code' ? css.stepDotActive : ''}`}
        />
      </div>

      {/* Heading */}
      <div className={css.headingArea}>
        <h1 className={css.title}>
          {authStep === 'phone' ? 'Welcome back' : 'Enter your code'}
        </h1>
        <p className={css.subtitle}>
          {authStep === 'phone'
            ? 'Sign in to your admin dashboard'
            : `Verification code sent to ${phoneNumber}`}
        </p>
      </div>

      {/* Error banner */}
      {(localError || error) && (
        <div className={css.errorMessage}>
          <span className={css.errorIcon}>!</span>
          {localError ||
            (typeof error === "string"
              ? error
              : error.message || "An error occurred")}
        </div>
      )}

      {/* Phone step */}
      {authStep === "phone" && (
        <>
          <form onSubmit={handleSendCode} className={css.form}>
            <div className={css.fieldGroup}>
              <label className={css.fieldLabel}>Phone number</label>
              <div className={css.phoneInputWrap}>
                <span className={css.phoneIcon}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.86 9.81a19.79 19.79 0 01-3.07-8.67A2 2 0 012.77 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 7.91a16 16 0 006.1 6.1l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 14.92v2z" />
                  </svg>
                </span>
                <input
                  type="tel"
                  placeholder={`${phoneNumberExamples.georgia} or ${phoneNumberExamples.uae}`}
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  className={css.phoneInput}
                  disabled={loading}
                />
              </div>
              <small className={css.fieldHint}>
                Include country code — e.g. {phoneNumberExamples.georgia} (Georgia) or {phoneNumberExamples.uae} (UAE)
              </small>
            </div>

            <button
              type="submit"
              className={css.primaryButton}
              disabled={loading || status === "loading"}
            >
              {loading ? (
                <><span className={css.btnSpinner} />Sending code...</>
              ) : (
                'Send verification code'
              )}
            </button>
          </form>

          <div className={css.divider}>
            <span>or</span>
          </div>

          <button
            type="button"
            onClick={handleGoogleSignIn}
            className={css.googleButton}
            disabled={loading || status === "loading"}
          >
            <svg width="18" height="18" viewBox="0 0 18 18">
              <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" />
              <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z" />
              <path fill="#FBBC05" d="M3.964 10.707c-.18-.54-.282-1.117-.282-1.707s.102-1.167.282-1.707V4.961H.957C.347 6.175 0 7.55 0 9s.348 2.825.957 4.039l3.007-2.332z" />
              <path fill="#EA4335" d="M9 3.582c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.961L3.964 7.293C4.672 5.166 6.656 3.582 9 3.582z" />
            </svg>
            {loading ? 'Signing in...' : 'Continue with Google'}
          </button>

          {/* reCAPTCHA container — must stay in DOM during phone step */}
          <div id="recaptcha-container" />
        </>
      )}

      {/* OTP step */}
      {authStep === "code" && (
        <div className={css.otpSection}>
          <form onSubmit={handleVerifyCode} className={css.form}>
            <div className={css.fieldGroup}>
              <label className={css.fieldLabel}>6-digit verification code</label>
              <div className={css.otpBoxes} onPaste={handleOtpBoxPaste}>
                {Array.from({ length: 6 }, (_, i) => (
                  <input
                    key={i}
                    ref={(el) => { otpRefs.current[i] = el; }}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={verificationCode[i] || ''}
                    onChange={(e) => handleOtpBoxChange(i, e)}
                    onKeyDown={(e) => handleOtpBoxKeyDown(i, e)}
                    className={css.otpBox}
                    disabled={loading}
                  />
                ))}
              </div>
            </div>

            <button
              type="submit"
              className={css.primaryButton}
              disabled={loading || status === "loading"}
            >
              {loading ? (
                <><span className={css.btnSpinner} />Verifying...</>
              ) : (
                'Verify code'
              )}
            </button>
          </form>

          <button
            type="button"
            className={css.backLink}
            onClick={handleBackToPhone}
          >
            ← Change phone number
          </button>
        </div>
      )}

      <Link to="/forgot-password" className={css.helpLink}>
        Need help?
      </Link>
    </section>
  );
};

export { LoginComponent };
